/**
 * Owner-approved copy of the path-mode panel (.design/articles-path-mode/DESIGN_BRIEF.md, C-5…C-20, approved
 * 2026-10-07) and the C-16 client-validation mapping. UI layer only: the shared validator
 * (utils/publicBaseUrl.ts) and the server keep their own messages; this table only re-words what the UI shows.
 */
import { PUBLIC_BASE_URL_MAX_LENGTH, PUBLIC_BASE_URL_MESSAGES, type PublicBaseUrlInvalidReason } from '@modules/custom-domains/utils/publicBaseUrl';

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** C-6 article label: "{title} — /{alias}"; "/{alias}" when the title is unknown. */
export const articleLabel = (alias: string, title?: string | null): string => (title ? `${title} — /${alias}` : `/${alias}`);

export const PATH_MODE_COPY = {
  sectionTitle: 'Path on your main site',
  sectionSubtitle: 'Optional. Show articles at www.yourdomain.com/blog instead of the subdomain.',
  needsDomain: 'Connect and verify the article domain first',
  intro: (base: string) =>
    `Serve your articles under a path of your main site, for example ${base}/my-article. Your site forwards that path to Segmently; search engines see the articles as part of your site.`,
  introExampleBase: 'https://www.yourdomain.com/blog',
  status: {
    subdomain: 'Subdomain mode',
    active: (base: string) => `Path mode active — ${base}`,
    failed: (n: number) => `Path mode active — ${n} ${plural(n, 'article', 'articles')} failed`,
    switchingToPath: (n: number, total: number) => `Switching to path mode: ${n} of ${total} articles republished`,
    switchingToSubdomain: (n: number, total: number) => `Switching to subdomain mode: ${n} of ${total} articles republished`,
  },
  sub: {
    subdomain: (articleHost: string) => `Articles are served at https://${articleHost}/{alias}.`,
    verifying: (hostAndPath: string) => `Checking that ${hostAndPath} forwards to your articles…`,
    active: (base: string) => `Articles are served at ${base}/{alias}. Canonical links, sitemap, feed and related cards use this address.`,
    failed: (base: string, n: number) =>
      `Articles are served at ${base}/{alias}. ${n === 1 ? 'One article still carries' : `${n} articles still carry`} the old address until ${plural(n, 'it is', 'they are')} republished.`,
    running: 'Keep this page open. If you close it, the switch continues the next time this page is opened.',
  },
  input: {
    label: 'Base URL',
    placeholder: 'https://www.yourdomain.com/blog',
    helper: 'Your main site address plus the path, with no slash at the end.',
    readOnlyHelper: 'Disable path mode to change the base URL.',
  },
  buttons: {
    verify: 'Verify & enable',
    verifying: 'Verifying…',
    republishing: 'Republishing…',
    disable: 'Disable path mode',
    retryFailed: 'Republish failed articles',
    /** NEW (final review I-1, owner approval pending): re-run a switch that failed before republishing anything. */
    retrySwitch: 'Retry',
    /** NEW (fix round 1, owner approval pending): recovery after a failed Disable — republish from the Library. */
    openLibrary: 'Open Library',
    copy: 'Copy',
  },
  proxy: {
    title: 'Proxy rule for your site',
    intro: (siteHost: string, prefix: string, articleHost: string) =>
      `Pick where ${siteHost} is hosted and add this rule. It forwards ${prefix || '/'} and everything under it to ${articleHost}.`,
    beforeTyping: 'Type your base URL above — the proxy rule for your hosting appears here.',
    tabsLabel: 'Hosting provider',
    copyLabel: (provider: string) => `Copy ${provider} rule`,
  },
  robots: {
    title: 'robots.txt on your site',
    intro: 'In path mode your site serves robots.txt, not Segmently. Add this line so search engines find the article sitemap.',
    copyLabel: 'Copy robots.txt line',
  },
  report: {
    lastSwitch: 'Last switch',
    progress: 'Progress',
    chips: { republished: 'Republished', skipped: 'Skipped', failed: 'Failed', waiting: 'Waiting' },
    columns: { article: 'Article', result: 'Result', note: 'Note' },
    skippedNote: (staleUrl: string) => `Pending edits. The canonical stays ${staleUrl} until you publish it.`,
    rootRelativeNote: (n: number, siteHost: string) => `Has ${n} ${plural(n, 'link', 'links')} that start with “/”; they now point at ${siteHost}.`,
    relatedOk: 'Related cards and index lists refreshed.',
    relatedFailed: 'Related cards and index lists were not refreshed. Run Verify & enable again.',
    /** After a Disable: "Verify & enable" would switch back to path mode, so only the approved first sentence. */
    relatedFailedAfterClear: 'Related cards and index lists were not refreshed.',
    failedTitle: (n: number) => (n === 1 ? '1 article was not republished' : `${n} articles were not republished`),
    failedTail: (n: number) => `Fix the ${plural(n, 'article', 'articles')}, then republish the failed articles.`,
  },
  disableDialog: {
    title: 'Disable path mode?',
    body: (articleHost: string) =>
      `Your articles go back to https://${articleHost}/{alias}. We republish them so canonical links, sitemap and feed use the subdomain again.`,
    secondary: (siteHost: string) => `When it finishes, remove the proxy rule and the Sitemap line from ${siteHost}.`,
    /** D-4: the header X is the single dismiss; its accessible name is "Cancel". */
    dismissLabel: 'Cancel',
    confirm: 'Disable path mode',
  },
  home: {
    label: 'Home page',
    none: 'No home page',
    option: (alias: string, title?: string) => articleLabel(alias, title),
    helper: (articleHost: string) => `The article readers see at https://${articleHost}/ — and at your base URL in path mode.`,
  },
} as const;

/**
 * C-15: approved text of a failed Verify, per server error code (design review 2026-10-07). UI layer only, like C-16:
 * the server keeps its own messages (CLI, logs); the panel shows the owner-approved wording for the value it sent.
 * Codes without an approved text keep the server message.
 */
const C15: Record<string, (sent: string) => string> = {
  base_url_unreachable: (sent) => `We could not reach ${sent}. Check the address and that the site is online.`,
  base_url_timeout: (sent) => `${hostOf(sent)} did not answer within 5 seconds. Check the proxy rule and try again.`,
  base_url_not_proxied: () => 'This URL does not forward to your articles yet. Set up the proxy rule below, then verify again',
  base_url_wrong_project: () => 'This URL points to a different Segmently project',
  server_error: () => 'Something went wrong — try again',
};

function hostOf(value: string): string {
  try {
    return new URL(value).host;
  } catch {
    return value;
  }
}

/** Maps a failed Verify (`error.code`) to the approved C-15 text for the `sent` base URL. */
export function verifyErrorText(error: { code: string; message: string }, sent: string): string {
  const text = C15[error.code];
  return text ? text(sent.trim()) : error.message;
}

/** C-16: approved client-validation text per validator reason. `null` = no message (empty input: Verify is just disabled). */
const C16: Record<PublicBaseUrlInvalidReason, ((value: string) => string) | null> = {
  empty: null,
  too_long: () => `The URL is too long (${PUBLIC_BASE_URL_MAX_LENGTH} characters max).`,
  not_https: () => 'The URL must start with https://',
  trailing_slash: (value) => `Remove the slash at the end: ${value.trim().replace(/\/+$/, '')}`,
  query_or_fragment: () => 'Remove everything after ? or #.',
  userinfo: () => 'Remove the user name and password from the URL.',
  port: () => 'Remove the port number.',
  ip_literal: () => "Use your site's domain name, not an IP address.",
  path_characters: () => 'The path can use letters, digits, - _ . ~ and /.',
  // No C-16 entry: the validator's own text stays.
  malformed: () => PUBLIC_BASE_URL_MESSAGES.malformed,
  same_as_article_host: () => 'Use your main site, not the article domain.',
  segmently_host: () => 'This address is served by Segmently. Use your own site.',
};

const REASON_BY_MESSAGE = new Map<string, PublicBaseUrlInvalidReason>(
  (Object.entries(PUBLIC_BASE_URL_MESSAGES) as Array<[PublicBaseUrlInvalidReason, string]>).map(([reason, message]) => [message, reason]),
);

/**
 * Maps the hook's `clientError` (a validator message) to the approved C-16 text for the typed `value`.
 * Unknown messages pass through unchanged.
 */
export function clientValidationText(clientError: string | null, value: string): string | null {
  if (!clientError) return null;
  const reason = REASON_BY_MESSAGE.get(clientError);
  if (!reason) return clientError;
  const text = C16[reason];
  return text ? text(value) : null;
}
